import { useState } from "react";
import { withClientContext } from "~/apollo/client";
import {
  useApproveDdqForGpMutation,
  useCompleteBackfillDdqMutation,
  useMarkDdqReadyForReviewMutation,
  useReturnDdqForEditsMutation,
} from "~/graphql/supergraphHooks";

export type UseCheckerHandoffArgs = {
  // Undefined until the wizard's queue row loads.
  processTaskId: string | undefined;
  reviewTaskId: string | undefined;
  onMarkedReady: () => void;
  onApproved: () => void;
  onReturned: () => void;
};

export type UseCheckerHandoffResult = {
  onMarkReadyForReview: (checkerId: string) => void;
  isMarkingReady: boolean;
  markReadyError: string | null;
  onApproveForGp: () => void;
  isApproving: boolean;
  approveError: string | null;
  onCompleteBackfill: () => void;
  isCompletingBackfill: boolean;
  completeBackfillError: string | null;
  onReturnForEdits: (comment: string) => void;
  isReturning: boolean;
  returnError: string | null;
};

// mark-ready-for-review takes the PROCESS task's id (the maker's action); approve,
// complete-backfill, and return-for-edits all take the REVIEW task's id (the checker's own task).
export const useCheckerHandoff = ({
  processTaskId,
  reviewTaskId,
  onMarkedReady,
  onApproved,
  onReturned,
}: UseCheckerHandoffArgs): UseCheckerHandoffResult => {
  const [markReadyError, setMarkReadyError] = useState<string | null>(null);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [completeBackfillError, setCompleteBackfillError] = useState<string | null>(null);
  const [returnError, setReturnError] = useState<string | null>(null);

  const [markReady, { loading: isMarkingReady }] = useMarkDdqReadyForReviewMutation({
    context: withClientContext("federation"),
    refetchQueries: ["GetDdqProdOpsQueue"],
  });
  const [approve, { loading: isApproving }] = useApproveDdqForGpMutation({
    context: withClientContext("federation"),
    refetchQueries: ["GetDdqProdOpsQueue"],
  });
  const [completeBackfill, { loading: isCompletingBackfill }] = useCompleteBackfillDdqMutation({
    context: withClientContext("federation"),
    refetchQueries: ["GetDdqProdOpsQueue"],
  });
  const [returnForEdits, { loading: isReturning }] = useReturnDdqForEditsMutation({
    context: withClientContext("federation"),
    refetchQueries: ["GetDdqProdOpsQueue"],
  });

  const onMarkReadyForReview = (checkerId: string) => {
    if (processTaskId == null) return;
    setMarkReadyError(null);
    markReady({ variables: { taskId: processTaskId, checkerId } })
      .then(onMarkedReady)
      .catch((error: unknown) => {
        setMarkReadyError(error instanceof Error ? error.message : "Something went wrong.");
      });
  };

  const onApproveForGp = () => {
    if (reviewTaskId == null) return;
    setApproveError(null);
    approve({ variables: { taskId: reviewTaskId } })
      .then(onApproved)
      .catch((error: unknown) => {
        setApproveError(error instanceof Error ? error.message : "Something went wrong.");
      });
  };

  const onCompleteBackfill = () => {
    if (reviewTaskId == null) return;
    setCompleteBackfillError(null);
    completeBackfill({ variables: { taskId: reviewTaskId } })
      .then(onApproved)
      .catch((error: unknown) => {
        setCompleteBackfillError(
          error instanceof Error ? error.message : "Something went wrong.",
        );
      });
  };

  const onReturnForEdits = (comment: string) => {
    if (reviewTaskId == null) return;
    setReturnError(null);
    returnForEdits({ variables: { taskId: reviewTaskId, comment } })
      .then(onReturned)
      .catch((error: unknown) => {
        setReturnError(error instanceof Error ? error.message : "Something went wrong.");
      });
  };

  return {
    onMarkReadyForReview,
    isMarkingReady,
    markReadyError,
    onApproveForGp,
    isApproving,
    approveError,
    onCompleteBackfill,
    isCompletingBackfill,
    completeBackfillError,
    onReturnForEdits,
    isReturning,
    returnError,
  };
};
